import { RAWDATA } from 'business/constants';
import { VizType } from 'business/definitions';
import { camelize } from 'helpers/syntaxHelper';
import { useUserHasRole } from 'hooks/user';
import { trackEvent } from 'helpers/trackerHelpers';
import {
    getIsInitialBreadcrumb,
    getInitialBreadcrumbs,
    findNavigationTreeForSelectedAxis,
    getCurrentBreadcrumbNode,
    shouldTriggerBreadcrumbQuery,
    setNewBreadcrumbs,
    isNodeLastBreadcrumbElement,
} from '../helpers/breadcrumbs';
import {
    parseRawDataLastBreadcrumbPoint,
    parseRawDataPoint,
} from '../helpers/rawData';
import { BreadCrumbNodeT, NavigationTree } from '../types';
import { useTranslation } from 'react-i18next';

type ChartClickFunction = (
    point: ResponseDataT,
    selectedKey: keyof ResponseDataT,
) => void;
/**
 * The function that handles the chart clicks. It triggers the breadcrumbs query if needed, or sets the raw data modal point if the user has the right to do so.
 * @param {BreadCrumbNodeT[]} breadcrumbs - the chart's current breadcrumbs
 * @param {NavigationTree[] | undefined} navigationTrees - the navigation trees of the chart dimensions
 * @param {QueryFilterT[]} queryFilters - the chart's query filters
 * @param {React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>} setBreadcrumbs - setter of the chart's breadcrumbs
 * @param {React.Dispatch<React.SetStateAction<QueryFilterT | undefined>>} setNavigationFilter - setter of the chart's navigation filter. It is highly correlated with the breadcrumbs so we might want to merge them in the future.
 * @param {React.Dispatch<React.SetStateAction<ResponseDataT | undefined>>} setSelectedPoint - setter of the raw data modal point
 * @param {VizType} vizType - the chart viz type
 * @param {isFromArchivedDashboard} - is the chart in an archived dashboard
 * @returns {ChartClickFunction}
 */
export const useOnChartclick = (
    breadcrumbs: BreadCrumbNodeT[],
    navigationTrees: NavigationTree[] | undefined,
    queryFilters: QueryFilterT[],
    setBreadcrumbs: React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>,
    setNavigationFilter: React.Dispatch<
        React.SetStateAction<QueryFilterT | undefined>
    >,
    setSelectedPoint: React.Dispatch<
        React.SetStateAction<ResponseDataT | undefined>
    >,
    vizType: VizType,
    isFromArchivedDashboard?: boolean,
): ChartClickFunction => {
    const { t } = useTranslation();
    const isRawDataUser = useUserHasRole(RAWDATA);
    const isPivotTable = vizType === 'pivot_table';

    return (
        point: ResponseDataT | undefined,
        selectedKey?: keyof ResponseDataT,
    ) => {
        if (!point || isFromArchivedDashboard) {
            return;
        }
        const navigationTree = findNavigationTreeForSelectedAxis(
            selectedKey,
            navigationTrees,
        );
        const breadcrumbValue = selectedKey
            ? point[camelize(selectedKey)]
            : null;
        const breadcrumbNode = getCurrentBreadcrumbNode(
            breadcrumbValue,
            navigationTree,
        );
        const isInitialBreadcrumb = getIsInitialBreadcrumb(breadcrumbValue);

        const triggerBreadcrumbQuery =
            isInitialBreadcrumb ||
            shouldTriggerBreadcrumbQuery(
                breadcrumbNode,
                navigationTree,
                queryFilters,
            );
        if (isInitialBreadcrumb) {
            setBreadcrumbs(getInitialBreadcrumbs(t));
            setNavigationFilter(undefined);
            trackEvent('decision/indicator/chart-click-forage');
            return;
        }

        if (isNodeLastBreadcrumbElement(breadcrumbNode, breadcrumbs)) {
            setSelectedPoint(
                parseRawDataLastBreadcrumbPoint(
                    point,
                    navigationTree,
                    navigationTrees,
                ),
            );
        }

        if (triggerBreadcrumbQuery && breadcrumbNode && navigationTree) {
            setNewBreadcrumbs(
                breadcrumbNode,
                breadcrumbs,
                navigationTree,
                queryFilters,
                setBreadcrumbs,
                setNavigationFilter,
            );
            trackEvent('decision/indicator/chart-click-forage');
            return;
        }
        if (isRawDataUser && !isPivotTable) {
            setSelectedPoint(parseRawDataPoint(point, navigationTrees));
            return;
        }
        console.warn("The user doesn't have the Raw Data permission");
    };
};
